import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  getCorbeille,
  restaurerElement,
  retirerDeLaCorbeille,
} from '../services/corbeilleService';
import HistoriqueStatuts from '../components/HistoriqueStatuts';
import SelectRecherche from '../components/SelectRecherche';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import { notifierToast } from '../components/Toasts';
import { formatDateTime } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Corbeille.css';

/** Ce qui a été mis à la corbeille avec l'élément, en clair. */
const CONTENU = {
  devis: ['devis', 'devis'],
  contrats: ['contrat', 'contrats'],
  contrat_sessions: ['journée', 'journées'],
  contrat_participants: ['participant', 'participants'],
  documents_session: ['document', 'documents'],
  documents_client: ['pièce', 'pièces'],
  documents_formateur: ['pièce', 'pièces'],
  agenda_evenements: ['rendez-vous', 'rendez-vous'],
  calendrier: ['session INTER', 'sessions INTER'],
  formation_documents: ['document à fournir', 'documents à fournir'],
  qcm_questions: ['question', 'questions'],
  emargements: ['signature', 'signatures'],
};

function contenuSupprime(item) {
  return Object.entries(item.comptes || {})
    .filter(([table, n]) => table !== item.table && CONTENU[table] && n > 0)
    .map(([table, n]) => `${n} ${CONTENU[table][n > 1 ? 1 : 0]}`)
    .join(', ');
}

/**
 * La corbeille : tout ce qui a été supprimé depuis 30 jours. Rien n'est
 * effacé de la base — « Restaurer » remet l'élément et ce qui est parti avec
 * lui ; « Retirer » le fait sortir de la corbeille (il reste en base, sous le
 * statut « supprime_corbeille », et n'est plus affiché). Au bout de 30 jours,
 * un élément non restauré sort de la même façon.
 */
function Corbeille() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [jours, setJours] = useState(30);
  const [type, setType] = useState('');
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [travail, setTravail] = useState(null); // lot en cours, ou 'retrait'
  const [aRetirer, setARetirer] = useState(null); // item | 'tout'
  const [historiqueOuvert, setHistoriqueOuvert] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setRecherche(saisie), 300);
    return () => clearTimeout(t);
  }, [saisie]);

  const charger = useCallback(() => {
    setLoading(true);
    setErreur(null);
    return getCorbeille({ type, q: recherche })
      .then((res) => {
        setItems(res.data || []);
        setTypes(res.types || []);
        setJours(res.jours || 30);
      })
      .catch((err) => setErreur(err.message || 'Impossible de charger la corbeille.'))
      .finally(() => setLoading(false));
  }, [type, recherche]);

  useEffect(() => {
    charger();
  }, [charger]);

  async function restaurer(item) {
    setTravail(item.lot);
    try {
      const res = await restaurerElement(item.lot);
      notifierToast({ type: 'succes', message: res.message || 'Élément restauré.' });
      await charger();
    } catch (err) {
      notifierToast({ type: 'erreur', message: err.message || 'Restauration impossible.' });
    } finally {
      setTravail(null);
    }
  }

  async function retirer() {
    const cible = aRetirer;
    if (!cible) return;
    const lots = cible === 'tout' ? items.map((i) => i.lot) : [cible.lot];
    setTravail('retrait');
    try {
      const res = await retirerDeLaCorbeille(lots);
      notifierToast({ type: 'succes', message: res.message || 'Retiré de la corbeille.' });
      setARetirer(null);
      await charger();
    } catch (err) {
      notifierToast({ type: 'erreur', message: err.message || 'Retrait impossible.' });
    } finally {
      setTravail(null);
    }
  }

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Corbeille</h1>
          <p className="devis-subtitle">
            Les éléments supprimés restent restaurables {jours} jours. Passé ce délai, ou une fois
            retirés de la corbeille, ils ne sont plus affichés ; les fichiers produits ne sont
            jamais supprimés.
          </p>
        </div>
        <div className="devis-header-actions">
          <button
            type="button"
            className="devis-btn devis-btn-danger"
            disabled={!items.length || travail !== null}
            onClick={() => setARetirer('tout')}
          >
            Vider la corbeille
          </button>
        </div>
      </div>

      <div className="devis-toolbar">
        <input
          type="search"
          className="devis-search"
          placeholder="Rechercher un élément supprimé"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
        <SelectRecherche
          className="devis-select"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">Tous les types</option>
          {types.map((t) => (
            <option key={t.code} value={t.code}>
              {t.pluriel}
            </option>
          ))}
        </SelectRecherche>
        <span className="devis-total">
          {items.length} élément{items.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="devis-card">
        {loading && <div className="devis-state">Chargement…</div>}
        {!loading && erreur && <div className="devis-state devis-state-error">{erreur}</div>}
        {!loading && !erreur && items.length === 0 && (
          <div className="devis-empty">
            <div className="devis-empty-title">La corbeille est vide.</div>
            <div className="devis-empty-subtitle">
              Ce que vous supprimez apparaît ici pendant {jours} jours.
            </div>
          </div>
        )}

        {!loading && !erreur && items.length > 0 && (
          <div className="devis-table-wrap">
            <table className="devis-table devis-table-corbeille">
              <thead>
                <tr>
                  <th>ÉLÉMENT</th>
                  <th>TYPE</th>
                  <th>SUPPRIMÉ</th>
                  <th>RESTE</th>
                  <th className="devis-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const contenu = contenuSupprime(item);
                  return (
                    <Fragment key={item.lot}>
                      <tr className="devis-row">
                        <td>
                          <div className="corbeille-libelle">{item.libelle}</div>
                          {contenu && <div className="corbeille-contenu">avec {contenu}</div>}
                        </td>
                        <td data-label="TYPE">
                          <span className="corbeille-type">{item.type}</span>
                        </td>
                        <td data-label="SUPPRIMÉ">
                          <div>{formatDateTime(item.supprimeLe)}</div>
                          <div className="devis-status-detail">
                            {item.supprimeParNom ? `par ${item.supprimeParNom}` : 'automatique'}
                          </div>
                        </td>
                        <td data-label="RESTE">
                          <span
                            className={`corbeille-jours${
                              item.joursRestants <= 5 ? ' corbeille-jours-urgent' : ''
                            }`}
                          >
                            {item.joursRestants} jour{item.joursRestants > 1 ? 's' : ''}
                          </span>
                        </td>
                        <td>
                          <div className="corbeille-actions">
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-primary"
                              disabled={travail !== null}
                              onClick={() => restaurer(item)}
                            >
                              {travail === item.lot ? 'En cours' : 'Restaurer'}
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm"
                              onClick={() =>
                                setHistoriqueOuvert(historiqueOuvert === item.lot ? null : item.lot)
                              }
                            >
                              Historique
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-danger"
                              disabled={travail !== null}
                              onClick={() => setARetirer(item)}
                            >
                              Retirer
                            </button>
                          </div>
                        </td>
                      </tr>
                      {historiqueOuvert === item.lot && (
                        <tr className="corbeille-historique-ligne">
                          <td colSpan={5}>
                            <HistoriqueStatuts entite={item.entite} id={item.ligneId} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {aRetirer && (
        <ConfirmationSuppression
          titre={
            aRetirer === 'tout' ? 'Vider la corbeille ?' : 'Retirer cet élément de la corbeille ?'
          }
          sousTitre={aRetirer === 'tout' ? `${items.length} élément(s)` : aRetirer.libelle}
          details={[
            'Ils ne seront plus affichés ni restaurables depuis l’application.',
            'Les données restent en base (statut « supprime_corbeille ») et les fichiers produits restent dans le stockage.',
          ]}
          avertissement={
            <>
              <strong>Retrait de la corbeille.</strong> Plus de restauration possible.
            </>
          }
          libelleConfirmer="Oui, retirer"
          enCours={travail === 'retrait'}
          onAnnuler={() => setARetirer(null)}
          onConfirmer={retirer}
        />
      )}
    </div>
  );
}

export default Corbeille;
